import {
  loadBoardAuthoringService,
  type BoardServiceForOrg,
  type AuthoringServiceContext,
  type DryRunServices,
  type BoardAuthoringService,
} from "@/shared/domain/boards/boardAuthoringService";
import { RobotoDomainException } from "@/shared/http/exceptions";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import {
  classifyToolFailure,
  toolError,
  ToolErrorKind,
  type ClientToolDeps,
  type ClientToolExecutionResult,
} from "./ClientTools";

/**
 * The tool deps with every member a board tool reads present: the org, the
 * boards service, the create mutation, the services the source stages read
 * through ({@link dryRunServicesOf}), and the app's query client.
 */
export type BoardToolDeps = ClientToolDeps &
  Required<
    Pick<
      ClientToolDeps,
      | "orgId"
      | "boardService"
      | "createBoard"
      | "customFieldService"
      | "metricsService"
      | "queryService"
      | "sessionService"
      | "topicService"
      | "queryClient"
    >
  >;

/**
 * The deps a board tool was executed with, typed as {@link BoardToolDeps}.
 *
 * `ClientTool.execute` declares its deps optional, and most of their members
 * optional, because other tools run with fewer. The sidebar's tool executor
 * (`useClientToolExecution`) builds one deps object for every tool it runs,
 * with each of these members taken from the app's providers, and it starts no
 * batch while the chat has no organization. A board tool's `execute` states
 * that in the type by passing its deps through this function.
 */
export function boardToolDeps(deps: ClientToolDeps | undefined): BoardToolDeps {
  return deps as BoardToolDeps;
}

/**
 * Runs `body` with the board authoring service and the dry run's context, built
 * from the tool's deps: the services the source stages read through, the
 * app's query client, the org, and an abort signal nothing aborts. The authoring
 * service times the dry run itself (`BOARD_DRY_RUN_TIMEOUT_MS` in
 * `shared/domain/boards/boardAuthoringService.ts`) and reports a timeout in its
 * own words; a second timer here could abort the run first, which rejects it
 * with no report.
 *
 * Failures return the tool errors {@link withAuthoringService} lists.
 */
export function withDryRunContext(
  deps: BoardToolDeps,
  body: (
    service: BoardAuthoringService,
    ctx: AuthoringServiceContext,
  ) => Promise<ClientToolExecutionResult>,
): Promise<ClientToolExecutionResult> {
  return withAuthoringService((service) =>
    body(service, dryRunContextOf(deps)),
  );
}

/**
 * Runs `body` with the authoring service alone, for a tool that needs neither
 * the dry run's context nor the boards service.
 *
 * Each failure returns a tool error:
 * 1. An authoring service that fails to load returns an error saying nothing
 *    ran (`loadService`).
 * 2. An error `body` throws is classified the way the batch runner classifies
 *    one that escapes `execute`, and reported in a sentence written for the
 *    model (`reportingFailures`).
 */
export async function withAuthoringService(
  body: (
    service: BoardAuthoringService,
  ) => ClientToolExecutionResult | Promise<ClientToolExecutionResult>,
): Promise<ClientToolExecutionResult> {
  const loaded = await loadService();
  if ("failure" in loaded) {
    return loaded.failure;
  }
  return reportingFailures(() => body(loaded.service));
}

/**
 * Runs `body` with the authoring service and what reaching a saved board takes:
 * the boards service and the org that holds the boards. Reading a board runs no
 * queries and compiles no charts, so `body` gets no dry run context.
 *
 * Failures return the tool errors {@link withAuthoringService} lists.
 */
export function withBoardAccess(
  deps: BoardToolDeps,
  body: (
    service: BoardAuthoringService,
    boards: BoardServiceForOrg,
  ) => Promise<ClientToolExecutionResult>,
): Promise<ClientToolExecutionResult> {
  return withAuthoringService((service) =>
    body(service, boardServiceForOrgOf(deps)),
  );
}

/**
 * Runs `body` with the authoring service, the dry run's context, and what
 * reaching a saved board takes. Changing a board needs both: it dry-runs the
 * proposed definition against live data, and it reads and writes the board on
 * the platform.
 *
 * Failures return the tool errors {@link withAuthoringService} lists.
 */
export function withDryRunContextAndBoardAccess(
  deps: BoardToolDeps,
  body: (
    service: BoardAuthoringService,
    ctx: AuthoringServiceContext,
    boards: BoardServiceForOrg,
  ) => Promise<ClientToolExecutionResult>,
): Promise<ClientToolExecutionResult> {
  return withAuthoringService((service) =>
    body(service, dryRunContextOf(deps), boardServiceForOrgOf(deps)),
  );
}

type LoadedService =
  { service: BoardAuthoringService } | { failure: ClientToolExecutionResult };

/**
 * The authoring service, or the error to report when its chunk will not load.
 * Nothing has run at that point, so the result says so and carries no error
 * kind: the transport kind's meaning, that the outcome is unknown, would be
 * false here. The thrown error describes a script fetch, which the model cannot
 * act on, so it goes to error monitoring and not into the result.
 */
async function loadService(): Promise<LoadedService> {
  try {
    return { service: await loadBoardAuthoringService() };
  } catch (error) {
    ErrorMonitoringService.captureError(error);
    return {
      failure: toolError(
        "The board tools could not load in this browser tab, so nothing " +
          "ran and nothing was changed. Call again once. If it fails " +
          "again, tell the user the board tools are not loading and that " +
          "reloading the page usually fixes it.",
      ),
    };
  }
}

/**
 * Runs `body` and turns any error it throws into a tool error, classified the
 * way the batch runner classifies one that escapes `execute`; nothing
 * propagates. A refusal from the platform (a 4xx) carries a sentence written
 * for a person, which is passed on. Any other error describes the network, the
 * server or a bug, so it goes to error monitoring and the result says what the
 * model can do about an outcome it cannot see.
 */
async function reportingFailures(
  body: () => ClientToolExecutionResult | Promise<ClientToolExecutionResult>,
): Promise<ClientToolExecutionResult> {
  try {
    return await body();
  } catch (error) {
    if (error instanceof RobotoDomainException && error.isClientError()) {
      return toolError(error.message, {}, classifyToolFailure(error));
    }
    ErrorMonitoringService.captureError(error);
    return toolError(
      "The board tool stopped on a network or application fault, not on " +
        "anything in its input. A read or a dry run changed nothing and " +
        "can be called again. A create or an update may or may not have " +
        "taken effect: before sending it again, read the board, or list " +
        "boards, with client_read_board, or read the panel editor's draft " +
        "with client_read_panel_draft.",
      {},
      ToolErrorKind.TRANSPORT,
    );
  }
}

/**
 * The dry run's context from the tool deps. The context's signal belongs to a
 * controller no one keeps, so nothing aborts it.
 */
function dryRunContextOf(deps: BoardToolDeps): AuthoringServiceContext {
  return {
    orgId: deps.orgId,
    services: dryRunServicesOf(deps),
    queryClient: deps.queryClient,
    signal: new AbortController().signal,
  };
}

/** The services the source stages of a dry run read through, from the tool deps. */
export function dryRunServicesOf(deps: BoardToolDeps): DryRunServices {
  return {
    customFieldService: deps.customFieldService,
    metricsService: deps.metricsService,
    queryService: deps.queryService,
    sessionService: deps.sessionService,
    topicService: deps.topicService,
  };
}

/** What reaching a saved board takes, from the tool deps. */
function boardServiceForOrgOf(deps: BoardToolDeps): BoardServiceForOrg {
  return { boardService: deps.boardService, orgId: deps.orgId };
}
